import React, { FC } from 'react';
import { Product } from '~/types/product';
import { CollectionByHandle } from '~/types/dto/collectionsDto';
import { ProductSettings, SelectionControls } from '../SelectionControls';
import styled from 'styled-components';
import {
  BackButtonContainer,
  BackArrowIcon,
  ProductTitle,
  DescriptionContainer,
  PriceContainer,
  PriceText,
  OriginalPriceText,
  DesktopOnly,
  MobileOnly,
} from '../styles';
import CurrencyIcon from '~/components/CurrencyIcon';
import BackArrow from 'public/assets/icons/back_arrow.svg';
import { isDiscounted } from '~/lib/utils';
import InlineMatchWith from './InlineMatchWith';
import {
  GlowInTheDarkBadge,
  hasGlowInTheDarkTag,
} from '~/components/GlowInTheDarkBadge';
import { FREE_PRICE_LABEL } from '~/common/utils/currency';
import { useRecoilValue } from 'recoil';
import { regionInfoState } from '~/recoil/useRegion';
import { toStore } from '~/common/utils/region';

interface ProductInfoProps {
  product: Product;
  productSettings: ProductSettings;
  setProductSettings: React.Dispatch<React.SetStateAction<ProductSettings>>;
  variants: any[];
  productSizing: any;
  toggleSizingModal: () => void;
  collectionsByHandle?: CollectionByHandle[];
  productsByHandle?: Array<Product>;
  onBackClick: () => void;
  matching?: Product[];
}

export const ProductInfo: FC<ProductInfoProps> = ({
  product,
  productSettings,
  setProductSettings,
  variants,
  productSizing,
  toggleSizingModal,
  collectionsByHandle,
  productsByHandle,
  onBackClick,
  matching = [],
}) => {
  const { region, countryCode } = useRecoilValue(regionInfoState);
  const description = (
    <>
      <DescriptionContainer
        dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
      />
      <ShippingDisclaimer as="p">
        {toStore(region, countryCode).disclaimer}
      </ShippingDisclaimer>
    </>
  );

  return (
    <>
      <BackButtonContainer onClick={onBackClick}>
        <BackArrowIcon
          src={BackArrow}
          alt="Back to shop page"
          loading="eager"
        />
        BACK
      </BackButtonContainer>

      {hasGlowInTheDarkTag(product.tags) ? (
        <TitleBadgeStack>
          <ProductTitle>{product.title}</ProductTitle>
          <GlowInTheDarkBadge />
        </TitleBadgeStack>
      ) : (
        <ProductTitle>{product.title}</ProductTitle>
      )}

      <DesktopOnly>{description}</DesktopOnly>

      <PriceContainer>
        {Number(product.prices.maxPrice) > 0 && (
          <StyledCurrencyIcon
            currencyCode={product.prices.currencyCode}
            textStyles={{ fontSize: '1.5rem' }}
            variant="white"
          />
        )}
        <PriceText>
          {Number(product.prices.maxPrice) > 0
            ? Number(product.prices.maxPrice).toFixed(2)
            : FREE_PRICE_LABEL}
        </PriceText>
        {isDiscounted(product.prices) && (
          <OriginalPriceText>
            {Number(product.prices.compareAtPriceRange?.maxPrice).toFixed(2)}
          </OriginalPriceText>
        )}
      </PriceContainer>

      <SelectionControls
        productSizing={productSizing}
        product={product}
        variants={variants}
        productSettings={productSettings}
        setProductSettings={setProductSettings}
        toggleSizingModal={toggleSizingModal}
        collectionsByHandle={collectionsByHandle}
        productsByHandle={productsByHandle}
      />

      <MobileOnly>{description}</MobileOnly>

      {matching.length > 0 && (
        <DesktopOnly>
          <InlineMatchWith matching={matching} />
        </DesktopOnly>
      )}
    </>
  );
};

const StyledCurrencyIcon = styled(CurrencyIcon)`
  min-width: 1rem;
  height: 1.25rem;
`;

// Own component (not in the `.ts` styles file) so it gets a deterministic class.
const ShippingDisclaimer = styled(DescriptionContainer)`
  margin-top: 0;
`;

const TitleBadgeStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 20px;

  & > h1 {
    margin: 0;
  }

  @media ${({ theme }) => theme.breakpoints.sm} {
    gap: 12px;
  }
`;
